import React from 'react';
import { useSubmitCaseContext } from '../../context/SubmitCaseContext';
import { User } from 'firebase/auth';
import FullscreenLoader from '../common/FullscreenLoader';
import { useActor } from '@xstate/react';
import Typography from '../common/Typography';
import OptionList, { Option } from './components/OptionList';
import { CaseType } from '../../stateMachines/submitCase/caseType';
import { timer } from 'ionicons/icons';
import styled from 'styled-components';
import AlertBox from '../common/AlertBox';
import moment from 'moment-timezone';
import { formatCurrencyShort } from '../../util/currency';
import { IonButton, IonIcon } from '@ionic/react';
import Platform from '../common/Platform';
import usePriorityCaseTiming from '../../hooks/usePriorityCaseTiming';

interface CaseTypeSelectionProps {
  currentUser: User;
  value: CaseType;
  initialValue?: CaseType;
  onChange: (value: CaseType) => void;
  isLoading: boolean;
  onNext: () => void;
  onBack?: () => void;
  isPriorityAvailable: boolean;
  businessStartTime?: string;
  businessEndTime?: string;
  priorityFee?: number;
  title?: string;
  preventNextIfUnchanged?: boolean;
}

const Container = styled.div`
  padding: 0 24px 32px 24px;
  max-width: 600px;
  width: 100%;
`;

const ButtonContainer = styled.div`
  margin-top: 64px;
  margin-bottom: 32px;
  // padding: 0 8px;
  display: flex;
  justify-content: space-between;
`;
const AlertContainer = styled.div`
  margin: 16px 0;
`;
const PriorityFee = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: -20px;
  right: -20px;
  background-color: var(--ion-color-primary-shade);
  color: white;
  border: 5px solid white;
  // color: var(--ion-color-secondary);
  // padding: 16px 8px;
  border-radius: 50%;
  width: 75px;
  height: 75px;
  font-weight: bold;
  font-size: 22px;
`;

const ADMIN_TIMEZONE = 'America/Chicago';

function formatTime(timeStr: string) {
  const hasMinutes = !timeStr.endsWith(':00');
  const time = moment.tz(timeStr, 'HH:mm', ADMIN_TIMEZONE);
  return time.format(hasMinutes ? 'h:mm A' : 'h A');
}

function useCurrentTimeCT() {
  const [time, setTime] = React.useState(
    moment.tz(moment(), ADMIN_TIMEZONE).format('h:mm A')
  );
  React.useEffect(() => {
    const interval = setInterval(() => {
      setTime(moment.tz(moment(), ADMIN_TIMEZONE).format('h:mm A'));
    }, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  return time;
}

export const CaseTypeSelection: React.FC<CaseTypeSelectionProps> = ({
  currentUser,
  isLoading,
  value,
  initialValue,
  onChange,
  isPriorityAvailable,
  onNext,
  businessStartTime = '08:00',
  businessEndTime = '16:00',
  priorityFee = 0,
  title = 'Case Type',
  onBack,
  preventNextIfUnchanged,
}) => {
  const isPrioritySelected = value === CaseType.PRIORITY;
  const { dueBy, isAfterHours, isBeforeHours } = usePriorityCaseTiming(
    businessStartTime,
    businessEndTime,
    isPrioritySelected
  );
  const [touched, setTouched] = React.useState(false);
  const hasChanged = initialValue !== value;
  const currentTimeCT = useCurrentTimeCT();
  return (
    <FullscreenLoader show={isLoading}>
      <Container>
        <Platform.Desktop>
          <Typography
            as="h3"
            fontSize="20px"
            variant="secondary"
            margin="16px 0"
          >
            {title}
          </Typography>
        </Platform.Desktop>

        <OptionList>
          <Option
            inputProps={{
              name: 'selectPaymentType',
              value: CaseType.STANDARD,
              checked: value === CaseType.STANDARD,
              onChange: (e) => {
                setTouched(true);
                onChange(CaseType.STANDARD);
              },
            }}
            onClick={() => onChange(CaseType.STANDARD)} // needed for iOS
            label="Standard case"
            details="Guaranteed 24 hour turnaround (weekdays only)"
            // icon={
            //   <IonIcon
            //     icon={newspaper}
            //     color="secondary"
            //     style={{ width: '40px', height: '40px' }}
            //   />
            // }
            // iconAlignment="top"
          />
          <Option
            inputProps={{
              name: 'selectPaymentType',
              value: CaseType.PRIORITY,
              checked: value === CaseType.PRIORITY,
              onChange: (e) => {
                setTouched(true);
                onChange(CaseType.PRIORITY);
              },
              disabled: !isPriorityAvailable,
            }}
            onClick={() => onChange(CaseType.PRIORITY)} // needed for iOS
            label="Priority"
            details={
              <>
                2-hour guaranteed turnaround time if submitted between{' '}
                {formatTime(businessStartTime)} and{' '}
                {formatTime(businessEndTime)} CT
                <PriorityFee>+{formatCurrencyShort(priorityFee)}</PriorityFee>
              </>
            }
            subtext={`*Currently ${currentTimeCT} CT`}
            icon={
              <IonIcon
                icon={timer}
                color="secondary"
                style={{ width: '50px', height: '50px' }}
              />
            }
            iconAlignment="top"
          />
        </OptionList>

        {touched && !hasChanged && preventNextIfUnchanged && (
          <AlertContainer>
            <AlertBox
              type="info"
              text={'You must change the existing case type to continue.'}
            />
          </AlertContainer>
        )}

        {!isPriorityAvailable && (
          <AlertContainer>
            <AlertBox
              type="warning"
              text={<>Priority case submissions are temporarily unavailable.</>}
            />
          </AlertContainer>
        )}

        {(isAfterHours || isBeforeHours) && (
          <AlertContainer>
            <AlertBox
              type="warning"
              text={
                <>
                  It is currently outside of business hours.
                  <br />
                  <br />
                  {isPrioritySelected ? 'Priority' : 'Standard'} case
                  submissions will be read by {dueBy.format('h:mm A')} CT on{' '}
                  {dueBy.format('dddd, MMMM Do')}.
                  {/* ({dueBy.tz(moment.tz.guess()).format('h:mm A z')}). */}
                </>
              }
            />
          </AlertContainer>
        )}

        <ButtonContainer>
          {onBack && (
            <IonButton type="button" onClick={onBack} fill="clear">
              BACK
            </IonButton>
          )}

          <IonButton
            type="button"
            onClick={onNext}
            disabled={!hasChanged && preventNextIfUnchanged}
            style={{
              alignSelf: 'flex-end',
              minWidth: '120px',
              width: onBack ? 'auto' : '100%',
            }}
          >
            NEXT
          </IonButton>
        </ButtonContainer>
      </Container>
    </FullscreenLoader>
  );
};

const ControlledCaseTypeSelection: React.FC<{ currentUser: User }> = ({
  currentUser,
}) => {
  const { submitCaseService } = useSubmitCaseContext();
  const [state, send] = useActor(submitCaseService);
  const isLoading = state.matches({ caseType: 'load' });

  return (
    <CaseTypeSelection
      currentUser={currentUser}
      isLoading={isLoading}
      onNext={() => send({ type: 'NEXT' })}
      onChange={(value) => send({ type: 'SET_CASE_TYPE', caseType: value })}
      value={state.context.caseType}
      isPriorityAvailable={state.context.priorityAvailable}
      businessStartTime={state.context.businessStart}
      businessEndTime={state.context.businessEnd}
      priorityFee={state.context.priorityFeePrice}
    />
  );
};

export default ControlledCaseTypeSelection;
